// OverwriteConfirmModal.jsx
import { motion, AnimatePresence } from "framer-motion"
import { useTranslation } from "react-i18next"
import { getOrderId, getHotelName, getClientCost, getCurrency } from "../../utils/parsers/orders"

const OverwriteConfirmModal = ({ isOpen, orders = [], onConfirm, onClose }) => {
  const { t } = useTranslation()

  const duplicates = orders.filter((o) => o.already_imported)

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 16 }}
            transition={{ type: "spring", stiffness: 380, damping: 26 }}
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-5 pt-5 pb-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-center shrink-0">
                  <svg
                    className="w-5 h-5 text-amber-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-800">
                    {t("sync.overwrite_warning_title")}
                  </h3>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {t("sync.overwrite_warning_subtitle")}
                  </p>
                </div>
              </div>
            </div>

            {/* Duplicate list */}
            <div className="px-5 py-3 max-h-52 overflow-y-auto space-y-1.5">
              {duplicates.map((o) => (
                <div
                  key={getOrderId(o)}
                  className="flex items-center justify-between px-3 py-2 bg-amber-50 border border-amber-100 rounded-lg"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-mono text-xs font-bold text-blue-600 shrink-0">
                      #{getOrderId(o)}
                    </span>
                    <span className="text-xs text-gray-600 truncate">{getHotelName(o)}</span>
                  </div>
                  <span className="text-xs font-medium text-gray-500 shrink-0 ml-2">
                    {getClientCost(o)} {getCurrency(o)}
                  </span>
                </div>
              ))}
            </div>

            {/* Warning text */}
            <div className="px-5 py-3 bg-amber-50 border-t border-amber-100">
              <p className="text-xs text-amber-700 leading-relaxed">
                ⚠ {t("sync.overwrite_warning_description")}
              </p>
            </div>

            {/* Actions */}
            <div className="px-5 py-4 flex items-center justify-end gap-2">
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={onClose}
                className="px-4 py-2 rounded-xl text-xs font-semibold bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
              >
                {t("admin.cancel")}
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={onConfirm}
                className="px-4 py-2 rounded-xl text-xs font-bold bg-amber-500 hover:bg-amber-600 text-white transition-colors shadow-sm"
              >
                {t("sync.overwrite_confirm")}
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default OverwriteConfirmModal
